import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Modal, Pressable } from 'react-native';
import DataOps from '../../data/DataOps';
import NotificationOps from '../../data/NotificationOps';
import CommentsModal from './CommentsModal';
import PhotosModal from './PhotosModal';
import ModalHeader from './coach_view/ModalHeader';
import TabBar from './coach_view/TabBar';
import OverviewTab from './coach_view/OverviewTab';
import AttendanceTab from './coach_view/AttendanceTab';
import SummaryModal from './coach_view/SummaryModal';
import { AttendanceUpdateType, buildSessionDays, isDateInFuture } from './coach_view/utils';
import {
    loadSessionDataForSession,
    loadSessionSummary,
    saveSessionSummary,
    toggleAttendance as toggleAttendanceHandler,
    handleAddComment as handleAddCommentHandler,
    handleAddPhoto as handleAddPhotoHandler
} from './coach_view/dataHandlers';
import TimeOps from '../../data/TimeOps';

interface CoachViewModalProps {
    visible: boolean;
    onClose: () => void;
    session?: any;
    sessionId?: string;
    campId?: string;
    currentUser?: any;
    isCoach?: boolean;
    initialView?: 'comments' | 'photos';
    initialDate?: string;
    targetContentId?: string;
    targetUserId?: string;
}

const CoachViewModal: React.FC<CoachViewModalProps> = ({
    visible,
    onClose,
    session,
    sessionId,
    campId,
    currentUser,
    isCoach = true,
    initialView,
    initialDate,
    targetContentId,
    targetUserId
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [registeredUsers, setRegisteredUsers] = useState<any[]>([]);
    const [sessionSummary, setSessionSummary] = useState<any>({
        attendance: {},
        classData: {},
        studentData: {}
    });
    const [sessionDays, setSessionDays] = useState<string[]>([]);
    const [loadedSession, setLoadedSession] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const [initialNavigationDone, setInitialNavigationDone] = useState(false);
    const [commentsModalVisible, setCommentsModalVisible] = useState(false);
    const [photosModalVisible, setPhotosModalVisible] = useState(false);
    const [currentModalContext, setCurrentModalContext] = useState<{
        type: 'class' | 'student';
        date: string;
        userId?: string;
        title: string;
    } | null>(null);
    const [summaryModalVisible, setSummaryModalVisible] = useState(false);
    const [summaryContext, setSummaryContext] = useState<{ userId?: string } | null>(null);

    useEffect(() => {
        try { (window as any).__coachModalOpen = !!visible; } catch {}
        if (visible) {
            if (session) {
                setLoadedSession(session);
                loadData(session);
            } else if (sessionId && campId) {
                loadSessionFromId();
            }
        }
        return () => { try { (window as any).__coachModalOpen = false; } catch {} };
    }, [visible, session, sessionId, campId]);

    const loadSessionFromId = async () => {
        if (!sessionId || !campId) {
            return;
        }

        setLoading(true);
        try {
            const sessionData = await DataOps.fetchDataFromFirebase('session', sessionId);
            if (sessionData) {
                setLoadedSession(sessionData);
                await loadData(sessionData);
            }
        } catch (error) {
            // no-op
        } finally {
            setLoading(false);
        }
    };

    const loadData = async (sessionData: any) => {
        try {
            // Compute days synchronously so tabs render immediately — no network round-trip needed.
            const days = buildSessionDays(sessionData.startDate, sessionData.endDate, TimeOps as any);
            setSessionDays(days);

            // Fetch registrations and attendance data in the background.
            const { children } = await loadSessionDataForSession(sessionData);
            setRegisteredUsers(children);

            const currentSessionId = sessionId || sessionData.sessionId;
            const result = await loadSessionSummary(currentSessionId, days, children);
            if (result) {
                setSessionSummary(result.summary);
                if (result.needsCleanupSave) {
                    await saveSessionSummaryWrapper(result.summary);
                }
            }
        } catch (error) {
            // no-op
        }
    };

    useEffect(() => {
        if (visible && !initialNavigationDone && sessionDays.length > 0) {
            if (initialView) {
                handleInitialViewNavigation();
            } else if (initialDate) {
                navigateToDate(initialDate);
            }
            setInitialNavigationDone(true);
        }
    }, [visible, initialView, initialDate, sessionDays, currentUser, targetContentId, targetUserId, sessionSummary]);

    const navigateToDate = (date: string) => {
        let targetDate = date;

        if (sessionDays.includes(date)) {
            targetDate = date;
        } else {
            try {
                const dateObj = new Date(date);
                if (!isNaN(dateObj.getTime())) {
                    const convertedDate = TimeOps.formatToMMDDYYYY(dateObj);
                    if (sessionDays.includes(convertedDate)) {
                        targetDate = convertedDate;
                    }
                }
            } catch (error) {
                // Use date as-is
            }
        }

        const dayIndex = sessionDays.indexOf(targetDate);
        if (dayIndex >= 0) {
            setActiveTab(dayIndex + 1);
        }
    };

    const handleInitialViewNavigation = () => {
        let context = null;

        if (targetContentId) {
            const contentType = initialView === 'comments' ? 'comments' : 'photos';
            const foundContext = findContentById(targetContentId, contentType, targetUserId);
            if (foundContext) {
                context = foundContext;
            }
        }

        if (!context) {
            let targetDate = initialDate;

            if (initialDate) {
                if (sessionDays.includes(initialDate)) {
                    targetDate = initialDate;
                } else {
                    try {
                        const dateObj = new Date(initialDate);
                        if (!isNaN(dateObj.getTime())) {
                            const convertedDate = TimeOps.formatToMMDDYYYY(dateObj);
                            targetDate = sessionDays.includes(convertedDate) ? convertedDate : sessionDays[0];
                        } else {
                            targetDate = sessionDays[0];
                        }
                    } catch (error) {
                        targetDate = sessionDays[0];
                    }
                }
            } else {
                targetDate = sessionDays[0];
            }

            context = !targetUserId ? {
                type: 'class' as const,
                date: targetDate,
                title: initialView === 'comments' ? 'Comments' : 'Moments'
            } : {
                type: 'student' as const,
                date: targetDate,
                userId: targetUserId,
                title: initialView === 'comments' ? 'Comments' : 'Moments'
            };
        }

        if (context) {
            setCurrentModalContext(context);
            const dayIndex = sessionDays.indexOf(context.date);
            if (dayIndex >= 0) {
                setActiveTab(dayIndex + 1);
            }

            if (initialView === 'comments') {
                setCommentsModalVisible(true);
            } else if (initialView === 'photos') {
                setPhotosModalVisible(true);
            }
        }
    };

    const findContentById = (contentId: string, contentType: 'comments' | 'photos', userId?: string) => {
        for (const date of sessionDays) {
            const classContent = sessionSummary.classData[date]?.[contentType] || [];
            if (Array.isArray(classContent) && classContent.some(item => item.id === contentId)) {
                return {
                    type: 'class' as const,
                    date,
                    title: contentType === 'comments' ? 'Comments' : 'Moments'
                };
            }

            if (userId) {
                const studentContent = sessionSummary.studentData[userId]?.[date]?.[contentType] || [];
                if (Array.isArray(studentContent) && studentContent.some(item => item.id === contentId)) {
                    return {
                        type: 'student' as const,
                        date,
                        userId,
                        title: contentType === 'comments' ? 'Comments' : 'Moments'
                    };
                }
            } else {
                for (const studentId of Object.keys(sessionSummary.studentData || {})) {
                    const studentContent = sessionSummary.studentData[studentId]?.[date]?.[contentType] || [];
                    if (Array.isArray(studentContent) && studentContent.some(item => item.id === contentId)) {
                        return {
                            type: 'student' as const,
                            date,
                            userId: studentId,
                            title: contentType === 'comments' ? 'Comments' : 'Moments'
                        };
                    }
                }
            }
        }
        return null;
    };

    const saveSessionSummaryWrapper = async (newSummary: any) => {
        const currentSession = loadedSession || session;
        const currentSessionId = sessionId || currentSession?.sessionId;
        await saveSessionSummary(currentSessionId, newSummary);
        setSessionSummary(newSummary);
    };

    const saveClassData = async (date: string, type: 'comments' | 'photos' | 'summary', data: any) => {
        const newSummary = {
            ...sessionSummary,
            classData: {
                ...sessionSummary.classData,
                [date]: {
                    ...sessionSummary.classData[date],
                    [type]: data
                }
            }
        };

        await saveSessionSummaryWrapper(newSummary);

        if ((type === 'comments' || type === 'photos') && isCoach) {
            try {
                const currentSession = loadedSession || session;
                const sessionTitle = currentSession?.title || 'Session';
                const formattedDate = new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

                const parentIds = Array.from(new Set(
                    registeredUsers.map(u => u.parentId).filter(Boolean)
                ));

                for (const parentUserId of parentIds) {
                    if (!parentUserId) continue;
                    if (type === 'comments') {
                        await NotificationOps.createNotification(
                            parentUserId,
                            `Coach posted a class comment in ${sessionTitle} on ${formattedDate}`,
                            'class_comment',
                            sessionId || currentSession?.sessionId,
                            campId || currentSession?.campId,
                            { actionDate: date, contentType: 'comment' }
                        );
                    } else if (type === 'photos') {
                        await NotificationOps.createNotification(
                            parentUserId,
                            `Coach shared class moments in ${sessionTitle} on ${formattedDate}`,
                            'class_photo',
                            sessionId || currentSession?.sessionId,
                            campId || currentSession?.campId,
                            { actionDate: date, contentType: 'photo' }
                        );
                    }
                }
            } catch (error) {
                // no-op
            }
        }
    };

    const saveStudentData = async (userId: string, date: string, type: 'comments' | 'photos' | 'summary', data: any) => {
        const newSummary = {
            ...sessionSummary,
            studentData: {
                ...sessionSummary.studentData,
                [userId]: {
                    ...sessionSummary.studentData[userId],
                    [date]: {
                        ...sessionSummary.studentData[userId]?.[date],
                        [type]: data
                    }
                }
            }
        };

        await saveSessionSummaryWrapper(newSummary);
    };

    const handleToggleAttendance = async (userId: string, date: string, type: AttendanceUpdateType) => {
        // Future session days are view-only.
        if (isDateInFuture(date)) return;

        const currentSession = loadedSession || session;
        await toggleAttendanceHandler(
            userId,
            date,
            type,
            sessionSummary,
            registeredUsers,
            currentSession,
            sessionId,
            campId,
            currentUser,
            saveSessionSummaryWrapper
        );
    };

    const handleAddComment = async (text: string) => {
        const currentSession = loadedSession || session;
        await handleAddCommentHandler(
            text,
            currentModalContext,
            isCoach,
            currentUser,
            sessionSummary,
            registeredUsers,
            currentSession,
            sessionId,
            campId,
            saveClassData,
            saveStudentData
        );
    };

    const handleAddPhoto = async (uri: string, caption?: string) => {
        const currentSession = loadedSession || session;
        await handleAddPhotoHandler(
            uri,
            caption,
            currentModalContext,
            isCoach,
            currentUser,
            sessionSummary,
            registeredUsers,
            currentSession,
            sessionId,
            campId,
            saveClassData,
            saveStudentData
        );
    };

    const handleClassAction = (date: string, type: 'comments' | 'photos') => {
        setCurrentModalContext({
            type: 'class',
            date,
            title: type === 'comments' ? 'Comments' : 'Moments'
        });
        if (type === 'comments') {
            setCommentsModalVisible(true);
        } else {
            setPhotosModalVisible(true);
        }
    };

    const handleStudentAction = (userId: string, date: string, type: 'comments' | 'photos') => {
        setCurrentModalContext({
            type: 'student',
            date,
            userId,
            title: type === 'comments' ? 'Comments' : 'Moments'
        });
        if (type === 'comments') {
            setCommentsModalVisible(true);
        } else {
            setPhotosModalVisible(true);
        }
    };

    const getCurrentComments = () => {
        if (!currentModalContext) return [];
        if (currentModalContext.type === 'class') {
            const classComments = sessionSummary.classData[currentModalContext.date]?.comments || [];
            return Array.isArray(classComments) ? classComments : [];
        } else {
            const studentComments = sessionSummary.studentData[currentModalContext.userId!]?.[currentModalContext.date]?.comments || [];
            return Array.isArray(studentComments) ? studentComments : [];
        }
    };

    const getCurrentPhotos = () => {
        if (!currentModalContext) return [];
        if (currentModalContext.type === 'class') {
            const classPhotos = sessionSummary.classData[currentModalContext.date]?.photos || [];
            return Array.isArray(classPhotos) ? classPhotos : [];
        } else {
            const studentPhotos = sessionSummary.studentData[currentModalContext.userId!]?.[currentModalContext.date]?.photos || [];
            return Array.isArray(studentPhotos) ? studentPhotos : [];
        }
    };

    const handleSummaryPress = (userId: string) => {
        setSummaryContext({ userId });
        setSummaryModalVisible(true);
    };

    const currentSession = loadedSession || session;

    if (!currentSession && loading) {
        return (
            <Modal visible={visible} animationType="slide" transparent={true}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContainer}>
                        <View style={styles.loadingContainer} />
                    </View>
                </View>
            </Modal>
        );
    }

    if (!currentSession) {
        return null;
    }

    const handleClose = () => {
        try { (window as any).__coachModalOpen = false; } catch {}
        onClose();
    };

    return (
        <Modal visible={visible} animationType="slide" transparent={true}>
            <View style={styles.modalOverlay}>
                {/* Fullscreen backdrop to prevent clicks from reaching underlying page */}
                <Pressable style={styles.backdrop} onPress={() => {}} />
                <View style={styles.modalContainer}>
                    <ModalHeader
                        sessionTitle={currentSession?.title || 'Session'}
                        isCoach={isCoach}
                        onClose={handleClose}
                    />

                    <TabBar
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        sessionDays={sessionDays}
                    />

                    <View style={styles.contentContainer}>
                        {activeTab === 0 ? (
                            <OverviewTab
                                session={currentSession}
                                registeredUsers={registeredUsers}
                                sessionDays={sessionDays}
                                sessionSummary={sessionSummary}
                                isCoach={isCoach}
                                currentUser={currentUser}
                                loading={loading}
                                onSummaryPress={handleSummaryPress}
                            />
                        ) : (
                            <AttendanceTab
                                date={sessionDays[activeTab - 1]}
                                registeredUsers={registeredUsers}
                                sessionSummary={sessionSummary}
                                isCoach={isCoach}
                                currentUser={currentUser}
                                onToggleAttendance={handleToggleAttendance}
                                onClassCommentPress={(date) => handleClassAction(date, 'comments')}
                                onClassPhotoPress={(date) => handleClassAction(date, 'photos')}
                                onStudentCommentPress={(userId, date) => handleStudentAction(userId, date, 'comments')}
                                onStudentPhotoPress={(userId, date) => handleStudentAction(userId, date, 'photos')}
                            />
                        )}
                    </View>
                </View>
            </View>

            <CommentsModal
                visible={commentsModalVisible}
                onClose={() => {
                    setCommentsModalVisible(false);
                    setCurrentModalContext(null);
                }}
                title={currentModalContext?.title || ''}
                comments={getCurrentComments()}
                onAddComment={handleAddComment}
                canAdd={isCoach && currentModalContext?.date ? !isDateInFuture(currentModalContext.date) : isCoach}
            />

            <PhotosModal
                visible={photosModalVisible}
                onClose={() => {
                    setPhotosModalVisible(false);
                    setCurrentModalContext(null);
                }}
                title={currentModalContext?.title || ''}
                photos={getCurrentPhotos()}
                onAddPhoto={handleAddPhoto}
                canAdd={isCoach && currentModalContext?.date ? !isDateInFuture(currentModalContext.date) : isCoach}
            />

            {summaryModalVisible && (
                <SummaryModal
                    visible={summaryModalVisible}
                    onClose={() => setSummaryModalVisible(false)}
                    sessionDays={sessionDays}
                    sessionSummary={sessionSummary as any}
                    userId={summaryContext?.userId}
                />
            )}
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
    },
    backdrop: {
        ...StyleSheet.absoluteFillObject,
    },
    modalContainer: {
        width: '90%',
        maxWidth: 900,
        height: '85%',
        backgroundColor: 'white',
        borderRadius: 20,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.25,
        shadowRadius: 20,
        elevation: 10,
    },
    contentContainer: {
        flex: 1,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
});

export default CoachViewModal;
